/* eslint-env browser */
import Analytics from '@ublend-npm/aula-analytics';
import clientInfo from '../../app/utils/clientInfo';

import {
  isInstructorInSpace,
  accessingSpaceAsStaff,
} from '../selectors/classroom';
import { getUserRole } from '../selectors/user';

import platform from './platform';
import performance from './performance';
import { ANALYTICS as analyticsEndpoint } from '../constants/endpoints';
import { getHoursBetweenDates } from './dates';
import * as analyticsTypes from '../constants/analytics';

const removeUnnecessaryFields = ({
  firstName,
  lastName,
  email,
  avatar,
  username,
  externalId,
  ...remainingProps
}) => remainingProps;

/**
 * @typedef {Object} SpaceData
 * @property {string} [activeClassroom] classRoom's objectId
 * @property {boolean} [isInstructor]
 * @property {boolean} [isAdmin]
 */

/**
 * @param {function} getState
 * @param {Object} options
 * @param {string} options.userId the user's objectId
 * @param {string} options.classRoomId the classRoom's objectId
 * @returns {SpaceData}
 */
const spaceDataFromState = (getState, { userId, classRoomId }) => {
  const state = getState();
  const isInstructor = isInstructorInSpace(state)(userId, classRoomId);
  const isAdmin = getUserRole(state) === 'Admin';
  const accessingAsStaff = accessingSpaceAsStaff(state)(classRoomId);
  return {
    activeClassroom: classRoomId,
    isInstructor,
    isAdmin,
    accessingAsStaff,
  };
};

/**
 * @param {{ hash }} location window.location object
 * @returns {SpaceData}
 */
const spaceDataFromLocation = (location) => {
  const matches = location.hash.match(/#\/dashboard\/(.*?)\//);
  return {
    activeClassroom: matches ? matches[1] : undefined,
  };
};

export const analyticsChangeClassRoom = (getState) => {
  const {
    classRoom: { current: classRoomId },
    user: userObj,
  } = getState();

  /** @type {SpaceData} */
  let spaceData = {};
  if (classRoomId && userObj) {
    const {
      user: { objectId: userId },
    } = userObj;
    spaceData = spaceDataFromState(getState, { userId, classRoomId });
  } else if (platform === 'web') {
    spaceData = spaceDataFromLocation(window.location);
  }

  let role;
  if (spaceData.isInstructor !== undefined) {
    if (spaceData.accessingAsStaff) {
      role = 'staff';
    } else if (spaceData.isInstructor) {
      role = 'educator';
    } else {
      role = 'student';
    }
  }

  Analytics.setClassRoom({
    ...spaceData,
    ...(role ? { role } : {}),
  });
};

export const analyticsChangeUser = (getState) => {
  const {
    user: { user },
  } = getState();
  const analyticsUser = user
    ? {
        userId: user.objectId,
      }
    : {};
  Analytics.setUser(analyticsUser);
};

export const analyticsChangeInstitution = (getState) => {
  const {
    institution: {
      general: { institution },
    },
  } = getState();

  if (Analytics.setInstitution) {
    Analytics.setInstitution(institution);
  }
  if (Analytics.setEndpoint) {
    Analytics.setEndpoint(analyticsEndpoint());
  }
  analyticsChangeUser(getState);
  analyticsChangeClassRoom(getState);
};

export const analyticsTrackEvent = (event, properties, user) =>
  Analytics.track({
    event,
    activeClassroom: undefined,
    ...(properties ? removeUnnecessaryFields(properties) : {}),
    ...(user ? removeUnnecessaryFields(user) : {}),
  });

/**
 * initializes the analytics with default params and user properties
 * @param {function} getState: function to get redux current state
 */
export const init = async (getState) => {
  const {
    institution: {
      general: { institution },
    },
  } = getState();

  await Analytics.init(institution, {}, platform, {}, { ...clientInfo });
  analyticsChangeInstitution(getState);
};

export const sendViewPostAnalytic = ({
  activeClassroom,
  postId,
  post,
} = {}) => {
  if (postId && post && activeClassroom) {
    const { important } = post;

    // Was the post created within the last 72 hours?
    const postAgeInHours = getHoursBetweenDates({
      startDate: post.createdAt,
      endDate: new Date(),
    });
    const isRecentPost = postAgeInHours >= 0 && postAgeInHours <= 72;

    analyticsTrackEvent(analyticsTypes.POST_VIEW, {
      activeClassroom,
      itemId: postId, // Keeping itemId as well, so that current dashboards for LI are not disturbed
      postId,
      isImportantPost: important,
      isRecentImportantPost: important && isRecentPost,
    });
  }
};

/**
 * Send a RECEIVE_PRESENTATION_UPDATE analytic event.
 *
 * @param {Object} props - Properties for this event
 * @param {string} props.spaceId - Id of the space
 * @param {string} props.presentationId - Id of the presentation
 * @param {string} props.presentationSessionId - Id of the presentation session
 * @param {string} props.correlationId - Correlation Id for this event
 */
export const sendReceivePresentationUpdateAnalytic = ({
  spaceId,
  presentationId,
  presentationSessionId,
  correlationId,
}) => {
  analyticsTrackEvent(analyticsTypes.RECEIVE_PRESENTATION_UPDATE, {
    spaceId,
    presentationId,
    presentationSessionId,
    correlationId,
  });
};

const getPerformanceKey = (eventType, correlationId) =>
  `${eventType}-${correlationId}`;

/**
 * Send a SHOW_PRESENTATION_NOTIFICATION analytic event.
 *
 * @param {Object} props - Properties for this event
 * @param {string} props.spaceId - Id of the space
 * @param {string} props.presentationId - Id of the presentation
 * @param {string} props.presentationSessionId - Id of the presentation session
 * @param {string} props.correlationId - Correlation Id for this event
 */
export const sendShowPresentationNotificationAnalytic = ({
  spaceId,
  presentationId,
  presentationSessionId,
  correlationId,
}) => {
  analyticsTrackEvent(analyticsTypes.SHOW_PRESENTATION_NOTIFICATION, {
    spaceId,
    presentationId,
    presentationSessionId,
    correlationId,
  });

  performance.mark(
    getPerformanceKey(
      analyticsTypes.SHOW_PRESENTATION_NOTIFICATION,
      correlationId
    )
  );
};

/**
 * Send a CLICK_PRESENTATION_NOTIFICATION analytic event.
 *
 * @param {Object} props - Properties for this event
 * @param {string} props.spaceId - Id of the space
 * @param {string} props.presentationId - Id of the presentation
 * @param {string} props.presentationSessionId - Id of the presentation session
 * @param {string} props.correlationId - Correlation Id for this event
 * @param {'full-screen'|'banner'} props.joinedPresentationFrom - The source of this event
 */
export const sendClickPresentationNotificationAnalytic = ({
  spaceId,
  presentationId,
  presentationSessionId,
  correlationId,
  joinedPresentationFrom,
}) => {
  let durationMs;

  if (joinedPresentationFrom === 'full-screen') {
    const startMark = getPerformanceKey(
      analyticsTypes.SHOW_PRESENTATION_NOTIFICATION,
      correlationId
    );
    const { duration } = performance.measure(
      'Time between seeing presentation notification, and clicking join',
      startMark
    );
    durationMs = Math.round(duration);
  }

  analyticsTrackEvent(analyticsTypes.CLICK_PRESENTATION_NOTIFICATION, {
    spaceId,
    presentationId,
    presentationSessionId,
    correlationId,
    joinedPresentationFrom,
    durationMs,
  });

  performance.mark(
    getPerformanceKey(
      analyticsTypes.CLICK_PRESENTATION_NOTIFICATION,
      correlationId
    )
  );
};

/**
 * Send a JOIN_PRESENTATION_SESSION analytic event.
 *
 * @param {Object} props - Properties for this event
 * @param {string} props.spaceId - Id of the space
 * @param {string} props.presentationId - Id of the presentation
 * @param {string} props.presentationSessionId - Id of the presentation session
 * @param {string} props.correlationId - Correlation Id for this event
 */
export const sendJoinPresentationSessionAnalytic = ({
  spaceId,
  presentationId,
  presentationSessionId,
  correlationId,
}) => {
  const startMark = getPerformanceKey(
    analyticsTypes.CLICK_PRESENTATION_NOTIFICATION,
    correlationId
  );
  const entries = performance.getEntriesByName(startMark);
  let duration;

  if (entries.length) {
    ({ duration } = performance.measure(
      'Time between clicking join presentation and seeing the first slide',
      startMark
    ));
  }

  analyticsTrackEvent(analyticsTypes.JOIN_PRESENTATION_SESSION, {
    spaceId,
    presentationId,
    presentationSessionId,
    correlationId,
    durationMs: duration ? Math.round(duration) : undefined,
  });
};

export default Analytics;
